import { makeAutoObservable, runInAction } from "mobx";
import { CREATE_RETURN_IN_INTEGRATION_TYPE, RETURN_METHOD_CATEGORY, RETURN_METHOD_TYPE, SHIPMENT_NOTIFICATION } from "../return-method-types";
import ReturnMethodsService, { IReturnMethodsSettings } from "../../../services/api/return-methods-service";
import { TYPE, rootContainer } from "../../../services/container/root";
import  ReturnMethodStore, { IReturnMethodChanges }  from "./return-method-store";
import ShippingIntegrationsService from "../../../services/api/shipping-integrarions-service";
import { uuid } from "../../../services/helpers";
import { IShippingConnection } from "../../../models/IntegrationsTypes";
import { DROP_OFF_LOCATION, RETURN_METHOD_CATEGORY_TO_TYPE_MAP } from "../constants";
import currentPortalStore from "../../../stores/current-portal-store";
import { FEATURES } from "../../../models/Plan";
import AggregatorsInfoStore from "./aggregators-info-store";
import { ILocaleItem } from "../../../translation/stores/translations-scope-store";


export class ReturnMethodsCategoryStore {
    public isExpanded: boolean = false;

    constructor(public category: RETURN_METHOD_CATEGORY, public list: ReturnMethodStore[]) {
        this.isExpanded = false;
        makeAutoObservable(this);
    }
}

export class ReturnMethodsListStore {

    private _aggregatorsInfoStore: AggregatorsInfoStore = {} as AggregatorsInfoStore;
    private _isLoading: boolean = false;
    private _isLoadingShippingConnections: boolean = false;
    private _isDeleting: boolean = false;
    private _selectedReturnMethodId?: number|string;
    private _settings: IReturnMethodsSettings = {} as IReturnMethodsSettings;
    private _shippingIntegrations: IShippingConnection[] = [] as IShippingConnection[];
    /** Capability-filtered list for return method connection selector; when absent, use full list. */
    private _shippingIntegrationsForReturnMethods: IShippingConnection[] | undefined = undefined;
    private _setTranslation: (key: string, translation: string, locale?: string | undefined, type?: string | undefined) => void = () => {};
    private _localeList: () => ILocaleItem[] = () => [];

    constructor() {
		makeAutoObservable(this);
	}

    returnMethodCategories: ReturnMethodsCategoryStore[] = [];


    private get returnMethodsService(): ReturnMethodsService {
		return rootContainer.get<ReturnMethodsService>(TYPE.ReturnMethodsService);
	}

    private get shippingIntegrationsService(): ShippingIntegrationsService {
        return rootContainer.get<ShippingIntegrationsService>(TYPE.ShippingIntegrationsService);
    }

    public get selectedReturnMethod(): ReturnMethodStore | undefined {
        return this.returnMethodCategories.find(category => 
            category.list.find(returnMethod => 
                returnMethod.isIdEquals(this._selectedReturnMethodId)
            )
        )?.list.find(returnMethod => 
            returnMethod.isIdEquals(this._selectedReturnMethodId)
        );
    }
    
    private get _availableCoverageConnection(): IShippingConnection|undefined {
        return this._shippingIntegrations.find(connection => this._aggregatorsInfoStore.isCoverage(connection.aggregator_code));
    }

    public getShippingIntegrations(): IShippingConnection[] {
        return this._shippingIntegrations;
    }

    /** List for return method connection selector; falls back to full list when backend does not send list_for_return_methods. */
    public getShippingIntegrationsForReturnMethods(): IShippingConnection[] {
        return this._shippingIntegrationsForReturnMethods ?? this._shippingIntegrations;
    }

    public setSelectedReturnMethodId(returnMethodId: number|string|undefined) {
        runInAction(() => {
            this._selectedReturnMethodId = returnMethodId;
        });
    }

    public get isLoading(): boolean {
        return this._isLoading || this._isDeleting || this._isLoadingShippingConnections;
    }

    private async _loadShippingConnections() {
        this._isLoadingShippingConnections = true;
        try {
            const shippingIntegrations = await this.shippingIntegrationsService.getIntegrations();
            runInAction(() => {
                this._isLoadingShippingConnections = false;
                this._shippingIntegrations = shippingIntegrations.list;
                this._shippingIntegrationsForReturnMethods = shippingIntegrations.list_for_return_methods;
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoadingShippingConnections = false;
            });        
        }
    }

    async load() {
        this._isLoading = true;
        try {
            const [returnMethods, ] = await Promise.all([
                this.returnMethodsService.getReturnMethodsList(),
                this._loadShippingConnections(),
                currentPortalStore.loadFeatures([FEATURES.PACKING_SLIP]),
            ]);
            runInAction(() => {
                this._isLoading = false;
                this._settings = returnMethods.settings;
                this._aggregatorsInfoStore = new AggregatorsInfoStore(this._settings.aggregators_info);
                this.returnMethodCategories = returnMethods.return_methods.map( returnMethodCategory => {
                    return new ReturnMethodsCategoryStore(
                        returnMethodCategory.category,
                        returnMethodCategory.list.map( returnMethod => {
                            return new ReturnMethodStore (
                                returnMethod, 
                                returnMethods.settings, 
                                this._aggregatorsInfoStore, 
                                (returnMethodStore) => this._removeReturnMethodFromList(returnMethodStore), 
                                (returnMethodStore) => this._updateCategoriesLists(returnMethodStore),
                                this._setTranslation,
                                this._localeList,
                                () => this._loadShippingConnections(),
                                () => this.getShippingIntegrations(),
                                () => this.getShippingIntegrationsForReturnMethods(),
                            );
                        })
                    );
                });
    
            });
        }
        catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });        
        }
	}

    private _updateCategoriesLists(returnMethod: ReturnMethodStore) {
        this._removeReturnMethodFromList(returnMethod);
        this._addReturnMethodToCategoryList(returnMethod);
    }

    private _addReturnMethodToCategoryList(returnMethod: ReturnMethodStore) {
        const category = Object.keys(RETURN_METHOD_CATEGORY_TO_TYPE_MAP).find(category =>
            RETURN_METHOD_CATEGORY_TO_TYPE_MAP[category as RETURN_METHOD_CATEGORY] === returnMethod.methodType);

            if (!category) {
            /** should never happen */
            throw new Error('_addReturnMethodToCategoryList did not find the category to edit');
        }

        this.returnMethodCategories.find(c => c.category === category)?.list.push(returnMethod);
    }

    private _removeReturnMethodFromList(returnMethodStore: ReturnMethodStore) {
        let returnMethodFound = false;
        this.returnMethodCategories.every(c => {
            const storeIndex = c.list.findIndex(store => store === returnMethodStore);

            if (storeIndex >= 0) {
                c.list.splice(storeIndex, 1);
                returnMethodFound = true;
                return false;
            }

            return true;
        })

        if (!returnMethodFound) {
            /** should never happen */
            throw new Error('removeRMFromList did not find the store to remove');
        }
    }

    public setTranslationMethods(
        setTranslation: (key: string, translation: string, locale?: string | undefined, type?: string | undefined) => void,
        localeList: () => ILocaleItem[]) {
        this._setTranslation = setTranslation;
        this._localeList = localeList;
    }

    public addReturnMethod(returnMethodCategory: RETURN_METHOD_CATEGORY): string {
        const clientId = uuid();
        const suffix = '_#' + clientId.split('-')[4];

        const newReturnMethod = new ReturnMethodStore(
            undefined, 
            this._settings, 
            this._aggregatorsInfoStore, 
            (returnMethodStore) => this._removeReturnMethodFromList(returnMethodStore), 
            (returnMethodStore) => this._updateCategoriesLists(returnMethodStore),
            this._setTranslation,
            this._localeList,
            () => this._loadShippingConnections(),
            () => this.getShippingIntegrations(),
            () => this.getShippingIntegrationsForReturnMethods(),
        );
        const methodType = RETURN_METHOD_CATEGORY_TO_TYPE_MAP[returnMethodCategory];
        const returnMethodCommonFields: IReturnMethodChanges = {
            _client_id: clientId,
            name: 'RETURN_METHOD' + suffix,
            display_name:'RETURN_METHOD_DISPLAYNAME' + suffix,
            description: 'RETURN_METHOD_DESCRIPTION' + suffix,
            instruction: 'RETURN_METHOD_INSTRUCTION' + suffix,
            use_dynamic_address: this._settings.should_use_dynamic_address,
            type: methodType,
            shipment_notification: methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL ? SHIPMENT_NOTIFICATION.ONLY_ON_SHIPMENT_RECEIVED_NOTIFICATION : SHIPMENT_NOTIFICATION.NONE_SHIPMENT_NOTIFICATION,
            shopify_restock_location_id: DROP_OFF_LOCATION.FULFILLMENT_LOCATION,
            create_return_in_wms: CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_NONE, 
        };

        newReturnMethod.setChanges(returnMethodCommonFields);
        
        /** Auto select coverage connection when enabled */
        if (newReturnMethod.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL && this._availableCoverageConnection) {
            newReturnMethod.shippingLabelSetting!.setShippingConnectionId(this._availableCoverageConnection.id);
        }

        const returnMethodsList = this.returnMethodCategories.find(c => c.category === returnMethodCategory);
        if (!returnMethodsList) {
            /** should never happen */
            throw new Error(`addReturnMethodn: did not find the list store for category ${returnMethodCategory}`);
        }
        returnMethodsList.list.push(newReturnMethod);

        return newReturnMethod.id as string;
    }
}

export default new ReturnMethodsListStore();